import { DataFetcher } from "@/components/display";
import { SchemaFormHook } from "@/components/form";
import { Dialog, DialogProps, ModalHeader } from "@/components/modal";
import { usePrinter } from "@/components/print";
import { GetCajaActualAperturaActualQuery } from "@/features/cajas/caja-actual/api/get-caja-actual-apertura-actual";
import CloseCajaForm, { closeCajaFormSchema } from "@/features/cajas/caja-actual/components/close-caja-form";
import { useCajaContext } from "@/features/cajas/caja-actual/contexts/caja-context";
import PrintCajaApertura from "@/features/cajas/shared/components/print-caja-apertura";
import useMonetizacion from "@/features/monetizacion/hooks/use-monetizacion";
import { getTransferenciaItem, getTransferenciaMetodoMonedaCantidad } from "@/features/monetizacion/shared/utils";
import { formatDate } from "@/shared/formats";
import { notify } from "@/shared/notification";
import { CajaApertura } from "@/shared/types/caja-apertura";
import { TransferenciaRequest } from "@/shared/types/transferencia";
import { useMemo } from "react";

export default function CloseCajaModal({ ...props }: DialogProps) {

    return <Dialog size="md" {...props}>
        <ModalHeader>Cerrar Caja</ModalHeader>
        <GetCajaActualAperturaActualQuery>
            {query => <DataFetcher query={query}>
                {cajaApertura => <CloseCajaModalForm cajaApertura={cajaApertura} />}
            </DataFetcher>}
        </GetCajaActualAperturaActualQuery>
    </Dialog>
}

function CloseCajaModalForm({
    cajaApertura
}: {
    cajaApertura: CajaApertura
}) {
    const { close } = useCajaContext();
    const printer = usePrinter();

    const { monedas: allMonedas, transferenciaMetodos: allTransferenciaMetodos } = useMonetizacion();

    const transferenciaReal: TransferenciaRequest = useMemo(() => {
        function isMetodoInTransferencia(transferencia: TransferenciaRequest, metodoId: string) {
            return Boolean(transferencia.items.find(ti => ti.transferenciaMetodoId === metodoId));
        }

        function isMonedaInTransferencia(transferencia: TransferenciaRequest, metodoId: string, monedaId: string) {
            return Boolean(getTransferenciaItem(transferencia, metodoId).monto.items.find(mi => mi.monedaId === monedaId))
        }

        const transferenciaMetodos = allTransferenciaMetodos.filter(tm => {
            if (!tm.hidden) return true;
            if (isMetodoInTransferencia(cajaApertura.transferenciaInicial, tm.id)) return true;
            if (isMetodoInTransferencia(cajaApertura.transferenciaTotal, tm.id)) return true;
            return false;
        })

        return {
            items: transferenciaMetodos.map(tm => {
                const monedas = allMonedas.filter(m => {
                    if (!m.hidden) return true;
                    if (isMonedaInTransferencia(cajaApertura.transferenciaInicial, tm.id, m.id)) return true;
                    if (isMonedaInTransferencia(cajaApertura.transferenciaTotal, tm.id, m.id)) return true;
                    return false;
                })

                return {
                    transferenciaMetodoId: tm.id,
                    monto: {
                        items: monedas.map(m => ({
                            monedaId: m.id,
                            cantidad: getTransferenciaMetodoMonedaCantidad(cajaApertura.transferenciaTotal, tm.id, m.id),
                        }))
                    }
                }
            })
        }
    }, [cajaApertura])

    return <>
        <SchemaFormHook
            schema={closeCajaFormSchema}
            onSubmit={async ({ value }) => {
                const cajaApertura = await close(value);
                await printer.print({
                    documentTitle: `Apertura de Caja - ${formatDate(cajaApertura.fechaApertura)}`,
                    printComponent: <PrintCajaApertura cajaApertura={cajaApertura} />
                });
                notify("La caja se ha cerrado correctamente.");
            }}
            defaultValues={{ transferenciaReal }}
            submitOnce
            closeModalOnSubmit
        >
            {form => <CloseCajaForm form={form} padding={3} cajaApertura={cajaApertura} />}
        </SchemaFormHook>
        <printer.Printer />
    </>
}